import type { MobileCheckoutIdentification } from 'src/queries/v2/cart/guest-identifications';
import type { SecondaryIdentificationServerResponse } from 'src/queries/v2/cart/secondary-identification';

type CustomerIdentificationDetails = {
  IsMedical: boolean;
  identifications: MobileCheckoutIdentification[];
};

/**
 * Mirrors Register's customer-type-aware primary-identification selection.
 * Selection is by type only; image fallback lives in `getIdentificationDisplayImage`.
 */
export const getPrimaryIdentification = (
  customer: CustomerIdentificationDetails,
): MobileCheckoutIdentification | undefined => {
  const targetType = customer.IsMedical ? 'MJStateIDNo' : 'DriversLicenseId';

  return (
    customer.identifications.find((identification) => identification.type === targetType) ??
    // Defensive fallback matching Register's historical behavior for malformed legacy payloads.
    customer.identifications[1]
  );
};

/**
 * Mirrors Register's `getAdditionalPrimaryIdentification`: the primary identification slot that does NOT
 * match the customer's type (drivers license for a medical customer, MMJ card for a recreational one).
 *
 * Deliberately ignores Register's per-dispensary field-option hiding (`isFieldHidden`). Those options hide an
 * ID *number* field on the profile form; Mobile Checkout does not load field options, and only renders a photo
 * here for staff ID verification. The untyped legacy photo lands on the `MJStateIDNo` slot regardless of the
 * document it actually shows, so hiding it because "MMJ ID" is hidden would recreate the missing-photo bug.
 */
export const getAdditionalPrimaryIdentification = (
  customer: CustomerIdentificationDetails,
): MobileCheckoutIdentification | undefined => {
  const otherType = customer.IsMedical ? 'DriversLicenseId' : 'MJStateIDNo';

  return customer.identifications.find((identification) => identification.type === otherType);
};

/**
 * The single ID image Mobile Checkout shows for a customer.
 *
 * Prefers the slot matching the customer type. When that slot has no image, falls back to the other primary
 * slot's image -- the same data Register surfaces as an additional identification card.
 *
 * Without this fallback, recreational customers whose only photo is the untyped legacy
 * `tbl_Accts.PatientPictureFileName` (uploaded before typed photos existed, or written by the PublicAPI patient
 * photo path) rendered a placeholder: guest details returns that legacy photo on the `MJStateIDNo` slot, so
 * the `DriversLicenseId` slot is empty. Register showed the photo; Mobile Checkout did not.
 */
export const getIdentificationDisplayImage = (customer: CustomerIdentificationDetails): string | null => {
  const primaryImage = getPrimaryIdentification(customer)?.image_url;
  if (primaryImage) {
    return primaryImage;
  }

  return getAdditionalPrimaryIdentification(customer)?.image_url || null;
};

/**
 * Last-resort ID image when neither primary identification slot has one: the first secondary identification
 * that actually has image bytes. Secondary identifications carry no document type, so there is no better
 * ordering than the API's; any photo of the customer's own ID beats a placeholder for staff verification.
 */
export const getFirstSecondaryIdentificationImage = (
  secondaryIdentifications: SecondaryIdentificationServerResponse[] | undefined,
): string | null => secondaryIdentifications?.find((identification) => !!identification.Image)?.Image ?? null;
